import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles, X } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface SinceLastVisit {
  first_visit: boolean
  since: string | null
  watched_vendors: string[]
  new_kev: { cve_id: string; vendor: string; product: string; date_added: string; watched: boolean }[]
  new_critical: { cve_id: string; summary: string; severity: string; vendors: string[]; watched: boolean }[]
  kev_count: number
  critical_count: number
  watched_kev_count: number
}

/** "What's new since you last visited" badge — pulls /api/me/since-last-visit
 * which returns KEV adds + critical CVEs published since the user's stored
 * last_seen_at. Watched-vendor matches are highlighted. Clicking opens a
 * dropdown summary; dismiss stamps "now" so the badge clears. */
export default function WhatsNewBadge() {
  const [data, setData] = useState<SinceLastVisit | null>(null)
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    api.get('/api/me/since-last-visit')
      .then(r => setData(r.data))
      .catch(() => setData(null))
  }, [])

  const dismiss = async () => {
    try {
      await api.post('/api/me/touch')
      setData(d => d ? { ...d, kev_count: 0, critical_count: 0, watched_kev_count: 0 } : d)
      setOpen(false)
    } catch {}
  }

  if (!data || data.first_visit) return null
  const total = data.kev_count + data.critical_count
  if (total === 0) return null

  const watchedHighlight = data.watched_kev_count > 0

  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(o => !o)}
        className={clsx('btn-ghost p-1 rounded-lg relative', watchedHighlight && 'animate-pulse')}
        title={`${total} new since you last visited`}>
        <Sparkles size={14} className={watchedHighlight ? 'text-rose' : 'text-amber-light'} />
        <span className={clsx(
          'absolute -top-1 -right-1 min-w-[14px] h-[14px] px-1 rounded-full text-[8px] font-bold flex items-center justify-center',
          watchedHighlight ? 'bg-rose text-white' : 'bg-amber-light text-black')}>
          {total > 99 ? '99+' : total}
        </span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 max-h-[480px] overflow-y-auto card p-3 z-[600] shadow-xl">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-text-primary flex items-center gap-1.5">
              <Sparkles size={12} className="text-amber-light" /> What's new
            </h3>
            <button type="button" onClick={dismiss} className="text-text-muted hover:text-text-primary text-[10px]"
              title="Mark as seen">
              <X size={12} />
            </button>
          </div>
          {data.since && (
            <p className="text-[10px] text-text-muted mb-2">
              since {new Date(data.since).toLocaleString()}
              {data.watched_vendors.length > 0 && (
                <> · watching <span className="font-mono">{data.watched_vendors.join(', ')}</span></>
              )}
            </p>
          )}

          {data.kev_count > 0 && (
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wider text-rose-light font-semibold mb-1">
                {data.kev_count} new KEV {data.watched_kev_count > 0 && <span>· {data.watched_kev_count} on watched vendors</span>}
              </div>
              <div className="space-y-1">
                {data.new_kev.slice(0, 8).map(k => (
                  <button key={k.cve_id} type="button"
                    onClick={() => { setOpen(false); navigate(`/app/cve/${encodeURIComponent(k.cve_id)}`) }}
                    className={clsx('w-full text-left text-[11px] p-1.5 rounded hover:bg-surface-hover',
                      k.watched && 'bg-rose/10 border border-rose/30')}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-blue-light">{k.cve_id}</span>
                      {k.watched && <span className="badge-red text-[9px]">watched</span>}
                    </div>
                    <div className="text-[10px] text-text-muted truncate">{k.vendor} {k.product}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {data.critical_count > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-amber-light font-semibold mb-1">
                {data.critical_count} new critical/high CVEs
              </div>
              <div className="space-y-1">
                {data.new_critical.slice(0, 6).map(c => (
                  <button key={c.cve_id} type="button"
                    onClick={() => { setOpen(false); navigate(`/app/cve/${encodeURIComponent(c.cve_id)}`) }}
                    className={clsx('w-full text-left text-[11px] p-1.5 rounded hover:bg-surface-hover',
                      c.watched && 'bg-amber/10 border border-amber/30')}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-blue-light">{c.cve_id}</span>
                      {c.watched && <span className="badge text-[9px] bg-amber/15 text-amber-light border border-amber/40">watched</span>}
                    </div>
                    <div className="text-[10px] text-text-muted truncate">{c.summary}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <button type="button" onClick={dismiss}
            className="btn-secondary text-[10px] w-full mt-3">
            Mark all as seen
          </button>
        </div>
      )}
    </div>
  )
}
